import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

/** Animated modal: portal, fade+scale enter transition, ESC + click-outside to close.
 *  `wide` sizes the panel for tables/threads instead of the compact list width. */
export function Modal({ title, subtitle, onClose, wide, children }: { title: string; subtitle?: string; onClose: () => void; wide?: boolean; children: ReactNode }) {
  const [visible, setVisible] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    restoreFocusRef.current = (document.activeElement as HTMLElement | null);
    const id = requestAnimationFrame(() => requestAnimationFrame(() => {
      setVisible(true);
      panelRef.current?.focus();
    }));
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      cancelAnimationFrame(id);
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
      restoreFocusRef.current?.focus?.();
    };
  }, [onClose]);

  if (typeof document === 'undefined') return null;
  return createPortal(
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-opacity duration-200 ${visible ? 'opacity-100' : 'opacity-0'}`}
      role="dialog"
      aria-modal="true"
    >
      {/* The click handler must live ON the backdrop: it covers the container, so a
          container-level target check never fires. */}
      <div className="absolute inset-0 bg-brand-dark/40 backdrop-blur-sm" onClick={onClose} />
      <div
        ref={panelRef}
        tabIndex={-1}
        className={`relative z-10 flex max-h-[82vh] w-full flex-col overflow-hidden rounded-brand bg-white shadow-2xl outline-none transition-all duration-200 ease-out ${wide ? 'max-w-[min(94vw,64rem)]' : 'max-w-lg'} ${visible ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}`}
      >
        <header className="flex items-start justify-between gap-4 border-b border-brand-primary/10 p-5">
          <div>
            <h3 className="font-heading text-lg font-semibold text-brand-dark">{title}</h3>
            {subtitle && <p className="mt-0.5 text-xs text-brand-dark/50">{subtitle}</p>}
          </div>
          <button type="button" onClick={onClose} className="text-brand-dark/40 transition-colors hover:text-brand-primary" aria-label="Luk">
            <X size={20} />
          </button>
        </header>
        <div className="overflow-auto p-5">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
